import { useState } from 'react';
import { MealPlanItem, Recipe } from '@/types/api';
import { useConfirmation } from '@/components/ui/confirmation-dialog';
import { OptimizedImage } from '@/components/ui/optimized-image';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, 
  MoreVertical, 
  Trash2, 
  Clock,
  Users,
  ImageIcon,
  Heart
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface MealSlotProps {
  day: Date;
  service: 'comida' | 'cena';
  mealPlanItem?: MealPlanItem;
  onEditServings?: (item: MealPlanItem) => void;
  onRemoveItem?: (item: MealPlanItem) => void;
  onReplaceRecipe?: (item: MealPlanItem) => void;
  onSlotSelect?: (day: Date, service: 'comida' | 'cena') => void;
  onViewRecipe?: (recipe: Recipe) => void;
  onToggleFavorite?: (recipe: Recipe) => void;
  isFavorite?: (recipeId: string) => boolean;
  className?: string;
  disabled?: boolean;
}

export function MealSlot({
  day,
  service,
  mealPlanItem,
  onEditServings: _onEditServings,
  onRemoveItem,
  onReplaceRecipe: _onReplaceRecipe,
  onSlotSelect,
  onViewRecipe,
  onToggleFavorite,
  isFavorite,
  className,
  disabled = false
}: MealSlotProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { confirm, ConfirmationComponent } = useConfirmation();

  const recipe = mealPlanItem?.recipe;
  const hasRecipe = !!recipe;

  const handleRemoveItem = () => {
    if (mealPlanItem) {
      confirm({
        title: 'Quitar receta del plan',
        description: `¿Estás seguro de que quieres quitar "${mealPlanItem.recipe.title}" de tu plan de comidas?`,
        confirmText: 'Sí, quitar',
        cancelText: 'Cancelar',
        variant: 'destructive',
        onConfirm: () => onRemoveItem?.(mealPlanItem),
      });
    }
    setIsMenuOpen(false);
  };

  const handleSlotClick = () => {
    if (!hasRecipe && !disabled && onSlotSelect) {
      onSlotSelect(day, service);
    } else if (hasRecipe && !disabled) {
      // En móvil, abrir menú al hacer clic en la card de receta
      setIsMenuOpen(true);
    }
  };

  const handleViewRecipe = () => {
    if (recipe) {
      onViewRecipe?.(recipe);
    }
    setIsMenuOpen(false);
  };

  const handleToggleFavorite = () => {
    if (recipe) {
      onToggleFavorite?.(recipe);
    }
    setIsMenuOpen(false);
  };

  return (
    <div
      onClick={handleSlotClick}
      className={cn(
        "relative min-h-[120px] p-3 rounded-lg border-2 border-dashed transition-all duration-200",
        "flex flex-col",
        hasRecipe 
          ? "border-solid border-gray-200 bg-white shadow-sm hover:shadow-md" 
          : "border-gray-300 bg-gray-50/50 hover:border-primary hover:bg-primary/5 cursor-pointer",
        disabled && "opacity-50 cursor-not-allowed",
        !hasRecipe && !disabled && onSlotSelect && "hover:shadow-md",
        className
      )}
    >
      {/* Menu de opciones (solo si tiene receta) */}
      {hasRecipe && !disabled && (
        <div className="absolute top-2 right-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity z-10">
          <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm"
                className="h-8 w-8 p-0 bg-white/90 backdrop-blur-sm shadow-md hover:bg-white 
                           touch-manipulation active:scale-95 transition-transform duration-150"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {/* Ver receta completa */}
              <DropdownMenuItem onClick={handleViewRecipe}>
                <ImageIcon className="h-4 w-4 mr-2" />
                Ver receta completa
              </DropdownMenuItem>
              
              {/* Añadir/quitar de favoritos */}
              <DropdownMenuItem onClick={handleToggleFavorite}>
                <Heart className={cn(
                  "h-4 w-4 mr-2",
                  recipe && isFavorite?.(recipe.id) ? "text-red-500 fill-current" : ""
                )} />
                {recipe && isFavorite?.(recipe.id) ? "Quitar de favoritos" : "Añadir a favoritos"}
              </DropdownMenuItem>
              
              <DropdownMenuSeparator />
              
              {/* TODO: Implementar ajustar porciones */}
              {/* TODO: Implementar mover a otro día */}
              
              {/* Eliminar del plan */}
              <DropdownMenuItem 
                onClick={handleRemoveItem}
                className="text-red-600 focus:text-red-600 focus:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Quitar del plan
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Content */}
      {hasRecipe ? (
        /* Recipe Content */
        <div className="flex-1 group">
          {/* Recipe Layout: Texto izquierda, imagen derecha */}
          <div className="flex gap-3 h-full">
            {/* Recipe Info - Left Side */}
            <div className="flex-1 min-w-0 flex flex-col justify-between">
              <div>
                <h4 className="text-sm font-medium text-gray-900 line-clamp-2 mb-2 leading-tight">
                  {recipe.title}
                </h4>
                
                {/* Recipe Meta */}
                <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
                  {recipe.timeMinutes && (
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>{recipe.timeMinutes}min</span>
                    </div>
                  )}
                  {mealPlanItem.servings && (
                    <div className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      <span>{mealPlanItem.servings}p</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Tags */}
              {recipe.tags && recipe.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {recipe.tags.slice(0, 2).map((tag) => (
                    <Badge 
                      key={tag.id}
                      variant="outline" 
                      className="text-xs px-1.5 py-0 h-auto"
                    >
                      {tag.tag}
                    </Badge>
                  ))}
                  {recipe.tags.length > 2 && (
                    <Badge 
                      variant="outline" 
                      className="text-xs px-1.5 py-0 h-auto text-gray-500"
                    >
                      +{recipe.tags.length - 2}
                    </Badge>
                  )}
                </div>
              )}
            </div>

            {/* Recipe Image - Right Side (Más grande) */}
            <div className="flex-shrink-0 w-20 h-16 sm:w-24 sm:h-20 rounded-lg overflow-hidden bg-gray-100 shadow-sm">
              <OptimizedImage
                recipe={recipe}
                alt={recipe.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                fallbackClassName="w-full h-full flex items-center justify-center"
                showLoadingSpinner={false}
              />
            </div>
          </div>
        </div>
      ) : (
        /* Empty State */
        <div className="flex-1 flex flex-col items-center justify-center text-center py-4">
          <div className="w-8 h-8 rounded-full flex items-center justify-center mb-2 transition-colors bg-gray-200 text-gray-400 hover:bg-primary hover:text-white">
            <Plus className="h-4 w-4" />
          </div>
          <p className="text-xs text-gray-500">
            Haz clic para agregar
          </p>
        </div>
      )}
      
      {/* Confirmation Dialog */}
      {ConfirmationComponent}
    </div>
  );
}
